import React, { useMemo } from 'react';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Container,
  makeStyles,
  Typography
} from '@material-ui/core';
import { Page } from 'src/components';
import { useFormik } from 'formik';
import { ChequeForm } from './components/ChequeForm';
import { useSnackBar } from 'src/hooks';
// import { useNavigate } from 'react-router';
import usePrompt from 'src/utils/navigation-prompt';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import {
  CreateChequeApiPayload,
  CreateChequePayload,
  PurchaseOrderItem
} from 'src/redux/slices/cheque-management/types';
import { initialChequeValues } from 'src/redux/slices/cheque-management/constants/enums';
import { useCheque } from 'src/hooks/cheque-management/use-cheque';
import { useNavigate } from 'react-router';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  productCard: {
    height: '100%'
  }
}));

export const component = () => {
  const classes = useStyles();
  const snackbar = useSnackBar();
  const navigate = useNavigate();

  const { isLoading, createCheque } = useCheque();

  const onPressCreate = async (values: CreateChequePayload) => {
    const payload: CreateChequeApiPayload = {
      ...values,
      po_nos: values?.po_nos?.map((po) => po.po_no)
    };

    const res = await createCheque(payload);

    if (res.success && res.id) {
      formikRef?.resetForm();
      navigate(`/app/cheque/${res.id}`);
    }
  };

  const handleSubmitWithValidation = async () => {
    const errors = await formikRef.validateForm();
    const errorsArr = Object.entries(errors) ?? [];
    const isGrandTotalAmountValid =
      Math.abs(
        Number(formikRef?.values?.amount || 0) +
          Number(formikRef?.values?.credit_memo_amount || 0) -
          Number(grandTotal || 0)
      ) < 0.01;

    if (!isGrandTotalAmountValid) {
      snackbar?.show({
        severity: 'error',
        message: 'The amount and credit memo total must match the grand total.',
        useSound: true
      });
      return;
    }

    if (errorsArr.length > 0) {
      snackbar.show({
        message: `${errorsArr[0][1]}`,
        severity: 'error'
      });
    } else {
      await formikRef.handleSubmit();
    }
  };

  // ts bandaid
  const formSchema: any = Yup.object().shape({
    cheque_no: Yup.string().required('Cheque No. is required'),
    pdc_date: Yup.date().required('PDC Date is required'),
    amount: Yup.number().required('Amount is required'),
    supplier_id: Yup.number().required('Supplier is required')
  });

  const formikRef = useFormik({
    initialValues: initialChequeValues,
    enableReinitialize: true,
    onSubmit: onPressCreate,
    validationSchema: formSchema
  });

  const grandTotal = useMemo(() => {
    const grand_total = (formikRef?.values?.po_nos || []).reduce(
      (accumulator: number, currentPo: PurchaseOrderItem) =>
        accumulator + (currentPo?.total_amount || currentPo?.amount || 0),
      0
    );

    return Math.round((grand_total + Number.EPSILON) * 100) / 100;
  }, [formikRef]);

  usePrompt(
    'There are some unsaved changes. Are you sure you want to leave?',
    formikRef.dirty
  );

  return (
    <Page className={classes.root} title="Create Purchase Orders">
      <Container maxWidth={false}>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          mb={1}
        >
          <Typography color="textPrimary" gutterBottom variant="h3">
            New Cheque
          </Typography>
        </Box>

        <ChequeForm
          grandTotal={grandTotal}
          formikRef={formikRef}
          isEditable={true}
          isCreatePage={true}
        />

        <Box
          mb={2}
          mt={4}
          display="flex"
          justifyContent="end"
          alignItems="center"
        >
          <Button
            disabled={isLoading}
            style={{ paddingTop: 16, paddingBottom: 16 }}
            color="primary"
            size="large"
            type="submit"
            variant="contained"
            onClick={handleSubmitWithValidation}
          >
            <NoteAddIcon style={{ marginRight: 8 }} />
            Create Cheque
          </Button>
        </Box>
      </Container>
    </Page>
  );
};

export const CreateCheque = React.memo(component);
